import { useLayoutEffect, useRef } from 'react';
import { ANIMATION_DURATIONS } from '../../../constants/animations';
import { isSdkStatus, isFullscreenStatus, isClosingStatus, STATUS_SDK } from '../../../constants/status';
import { useDeviceDetection } from './useDeviceDetection';

/**
 * Hook para gerenciar animação de altura de um container
 *
 * Anima suavemente quando o conteúdo muda de altura, mas evita animações redundantes
 * quando apenas o texto do GlobalProgress muda (altura não muda).
 *
 * Técnica utilizada:
 * 1. Captura altura atual (getBoundingClientRect)
 * 2. Permite conteúdo definir nova altura (height: auto)
 * 3. Captura nova altura
 * 4. Volta para altura antiga sem transição
 * 5. Anima para nova altura com transição CSS
 *
 * Otimizações:
 * - Usa dataset.showingGlobalProgress para detectar mudanças de tipo de conteúdo
 * - Se estava e continua mostrando GlobalProgress, não anima (apenas texto mudou)
 * - Usa getBoundingClientRect() para medições precisas (sub-pixel)
 * - Tolerância de 1px para evitar animações desnecessárias
 *
 * Tratamento de Resize:
 * - Adiciona listeners para eventos 'resize' e 'orientationchange'
 * - Implementa debounce de 150ms para otimizar performance durante resize
 * - Recalcula e reanima a altura do wrapper quando a janela muda de tamanho
 * - Garante que mudanças de orientação do dispositivo sejam tratadas adequadamente
 * - Remove listeners e cancela timeouts pendentes no cleanup
 * - Código refatorado com funções auxiliares para reduzir aninhamento (Sonar compliance)
 *
 * @param {string} displayStatusType - Tipo do status atual (null, "loading", "success", "error")
 * @param {string} displayLocationPathname - Pathname da location atual
 * @param {boolean} globalProgressBusy - Se o GlobalProgress está ocupado
 * @param {boolean} isShowingGlobalProgress - Se está exibindo o GlobalProgress (evita animar ao trocar apenas o texto do status)
 * @param {Object|null} status - Status prop original para verificar se é SDK
 * @returns {Object} Refs para o container
 * @returns {React.RefObject} returns.wrapperRef - Ref para o elemento wrapper (com animação de altura)
 * @returns {React.RefObject} returns.contentRef - Ref para o elemento de conteúdo (para medir altura)
 *
 * @example
 * const { wrapperRef, contentRef } = useHeightAnimation(
 *   displayStatus?.type,
 *   displayLocation.pathname,
 *   globalProgressBusy,
 *   isShowingGlobalProgress,
 *   status
 * );
 *
 * return (
 *   <div ref={wrapperRef} className="wrapper">
 *     <div ref={contentRef}>{content}</div>
 *   </div>
 * );
 */
export const useHeightAnimation = (
  displayStatusType,
  displayLocationPathname,
  globalProgressBusy,
  isShowingGlobalProgress,
  status = null,
  headerContentRef = null,
  isRenderingGlobalProgress = isShowingGlobalProgress,
) => {
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  const lastAnimationStateRef = useRef(null);
  const previousContentHeightRef = useRef(null); // Armazena altura do conteúdo anterior
  const resizeTimeoutRef = useRef(null); // Para debounce do resize
  const isFullscreenRef = useRef(isFullscreenStatus(status));
  // Same rationale as isFullscreenRef below: a height-animation timeout
  // scheduled before CLOSING became active must no-op when it fires, or its
  // `style.transition = 'height ...'` write clobbers the `transform`
  // transition useSlideAnimation just set (transition is a single shorthand
  // property — whichever hook writes it last wins).
  const isClosingRef = useRef(isClosingStatus(status));
  // Shared between the two height-animation sources (the status/route
  // effect, with its RENDER_DELAY delay, and the resize listener, with its
  // 150ms debounce). A resize that lands right after a status/route change
  // (e.g. an echo from a parent iframe adjusting to the new size) can
  // restart the transition mid-flight — resetting `transition: none` and
  // cutting off the content animation already in progress (visible as a
  // "flash": the height jumps, freezes for an instant, then animates for
  // real). The guard is asymmetric by design: the status/route effect is
  // the source of truth and is never blocked by it — only the resize path
  // checks the flag before restarting, deferring to the content animation
  // already in progress.
  const isAnimatingHeightRef = useRef(false);
  const animationEndTimeoutRef = useRef(null);
  // Set by executeResizeRecalculation when a resize is dropped by the guard
  // above. Without this, a resize landing during the guard window and never
  // followed by another one leaves the wrapper on a stale height until some
  // unrelated status/route/resize trigger comes along.
  const hasPendingResizeRef = useRef(false);

  const markHeightAnimationStarted = () => {
    isAnimatingHeightRef.current = true;
    if (animationEndTimeoutRef.current) {
      clearTimeout(animationEndTimeoutRef.current);
    }
    animationEndTimeoutRef.current = setTimeout(() => {
      isAnimatingHeightRef.current = false;
      animationEndTimeoutRef.current = null;
      if (hasPendingResizeRef.current) {
        hasPendingResizeRef.current = false;
        executeResizeRecalculation();
      }
    }, ANIMATION_DURATIONS.BASE);
  };
  const isDesktop = useDeviceDetection();

  isFullscreenRef.current = isFullscreenStatus(status);
  isClosingRef.current = isClosingStatus(status);

  // Quando um modal está aberto, o ModalRenderer aplica transform: scale() no
  // wrapper. Medir a altura nesse estado (via getBoundingClientRect) retorna
  // o valor JÁ escalado, e fixar essa altura encolhe o card permanentemente
  // (520 → 468 = ×0.9). Enquanto o wrapper estiver escalado, pulamos a
  // medição/animação de altura — a altura volta ao normal quando o modal
  // fecha e o transform é removido.
  const isWrapperScaled = () => {
    const t = wrapperRef.current?.style.transform || '';
    return t.includes('scale(');
  };

  // SDK/fullscreen guard: kept on its own effect, reacting to `status`
  // directly. Entering SDK/fullscreen updates `displayStatusType` in
  // lockstep with `status` in the real useStatusTransition wiring, but this
  // hook is also driven standalone in tests/other callers, so this branch
  // must stay responsive to `status` alone — see the main animation effect
  // below for why `status` was removed from *its* dependencies instead.
  useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }

    // Modal aberto (wrapper escalado): não remedir/animar a altura — a
    // medição viria escalada e encolheria o card. Ver isWrapperScaled.
    if (isWrapperScaled()) {
      return;
    }

    // SDK, fullscreen, and closing: skip height animation (managed directly
    // by DynamicWrapper / useSlideAnimation)
    if (!(isSdkStatus(status) || isFullscreenStatus(status) || isClosingStatus(status) || displayStatusType === STATUS_SDK)) {
      return;
    }

    // Marca que SDK foi exibido, para que a próxima transição anime corretamente
    const sdkState = `sdk-${displayLocationPathname}-${globalProgressBusy}`;
    if (lastAnimationStateRef.current !== sdkState) {
      lastAnimationStateRef.current = sdkState;
    }

    if (isFullscreenStatus(status) || isClosingStatus(status)) {
      // Cancel any pending height animation now — a stale timeout from an
      // in-flight loading/success transition would otherwise fire later and
      // overwrite the FLIP target (fullscreen) or the slideOut transition
      // useSlideAnimation just set (closing). The isFullscreenRef/isClosingRef
      // checks inside the timeouts below are the defensive backstop for the
      // rare case this effect somehow doesn't run first.
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    }

    if (isFullscreenStatus(status)) {
      // Freeze the current rendered height as an explicit inline style.
      // Without this, the wrapper may have height:'auto' (no previous animation ran)
      // or a CSS height transition may still be in progress from the route animation
      // (650ms). useFlipAnimation, which runs after this hook, calls
      // getBoundingClientRect() to capture the FLIP origin. An explicit pixel height
      // stops any ongoing CSS transition and gives it a stable, committed value —
      // the same guarantee the SDK pre-seed workaround provided externally.
      const inlineHeight = wrapperRef.current.style.height;
      if (!inlineHeight || inlineHeight === 'auto') {
        const currentHeight = wrapperRef.current.getBoundingClientRect().height;
        if (currentHeight > 0) {
          wrapperRef.current.style.height = `${currentHeight}px`;
        }
      }
    }
  }, [status, displayStatusType, displayLocationPathname, globalProgressBusy]);

  // Freeze-on-signal: `isRenderingGlobalProgress` mirrors ContentSwitcher's
  // own `shouldRenderGlobalProgress` (driven by `statusForProgress`), the
  // true, earliest signal that GlobalProgress is about to share the DOM with
  // the outgoing content this render — it changes synchronously, well before
  // `displayStatusType`/`isShowingGlobalProgress` do (those lag behind
  // useStatusTransition's fade-out sequence and GlobalProgress's own
  // busy-state reporting).
  //
  // Without this effect, the wrapper sits at `height: auto` for the entire
  // window between the raw status change and displayStatusType settling.
  // During that window ContentSwitcher renders the outgoing content AND the
  // incoming GlobalProgress as plain siblings (not an overlay), so an
  // auto-sized wrapper visibly grows to fit both — a real, CSS-driven
  // flicker, not a JS measurement bug (confirmed by direct measurement: a
  // 404px card briefly grows to 478px — content height + GlobalProgress's
  // fixed 74px — before the main effect below ever runs).
  //
  // This effect only ever reasserts the CURRENT height (never guesses a
  // future one), so it's safe to fire on every one of these early signals.
  // The main effect below is the only one that measures and animates to a
  // NEW height, and it correctly waits for displayStatusType to settle
  // before doing so.
  useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;
    if (isWrapperScaled()) return;
    if (isSdkStatus(status) || isFullscreenStatus(status) || isClosingStatus(status) || displayStatusType === STATUS_SDK) return;
    // True initial mount: let the main effect below set things up first —
    // freezing here would lock in an arbitrary pre-content height.
    if (lastAnimationStateRef.current === null) return;

    const hasInlineHeight =
      wrapperRef.current.style.height && wrapperRef.current.style.height !== 'auto';
    if (!hasInlineHeight) {
      const currentHeight = wrapperRef.current.getBoundingClientRect().height;
      if (currentHeight > 0) {
        wrapperRef.current.style.height = `${currentHeight}px`;
      }
    }
  }, [status, displayStatusType, isRenderingGlobalProgress]);

  useLayoutEffect(() => {
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }

    // Modal aberto (wrapper escalado): não remedir/animar a altura — a
    // medição viria escalada e encolheria o card. Ver isWrapperScaled.
    if (isWrapperScaled()) {
      return;
    }

    // SDK, fullscreen e closing: não anima altura (gerenciada pelo efeito acima)
    if (isSdkStatus(status) || isFullscreenStatus(status) || isClosingStatus(status) || displayStatusType === STATUS_SDK) {
      return;
    }

    // Cria um identificador único para este estado
    const currentState = `${displayStatusType}-${displayLocationPathname}-${globalProgressBusy}`;

    // Cancela qualquer animação pendente (Strict Mode pode executar 2x)
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    // Na montagem inicial (lastAnimationStateRef é null), não anima e deixa wrapper em height:auto
    // A altura será fixada somente quando a primeira transição acontecer
    // Usamos lastAnimationStateRef ao invés de isInitialMountRef para ser resiliente ao Strict Mode
    if (lastAnimationStateRef.current === null) {
      wrapperRef.current.dataset.showingGlobalProgress = isShowingGlobalProgress
        ? 'true'
        : 'false';

      // Aguarda conteúdo carregar (API, etc) e armazena altura inicial para primeira transição
      animationTimeoutRef.current = setTimeout(() => {
        if (!contentRef.current) return;
        /* istanbul ignore next */
        if (isFullscreenRef.current || isClosingRef.current) return;

        const initialHeight = contentRef.current.getBoundingClientRect().height;
        previousContentHeightRef.current = initialHeight;
      }, 500);

      // Marca que já processamos este estado inicial
      lastAnimationStateRef.current = currentState;

      return;
    }

    const wasShowingGlobalProgress =
      wrapperRef.current.dataset.showingGlobalProgress === 'true';

    // Se estava e continua mostrando GlobalProgress, não anima altura
    if (wasShowingGlobalProgress && isShowingGlobalProgress) {
      wrapperRef.current.dataset.showingGlobalProgress = 'true';
      lastAnimationStateRef.current = currentState;
      return;
    }

    // Atualiza o estado
    wrapperRef.current.dataset.showingGlobalProgress = isShowingGlobalProgress
      ? 'true'
      : 'false';

    // Determina se wrapper tem altura inline fixada
    const wrapperHasInlineHeight =
      wrapperRef.current.style.height &&
      wrapperRef.current.style.height !== 'auto';

    // Se wrapper perdeu a altura inline mas temos altura armazenada, fixa imediatamente
    if (!wrapperHasInlineHeight && previousContentHeightRef.current !== null) {
      wrapperRef.current.style.height = `${previousContentHeightRef.current}px`;
    }

    // Define oldHeight: usa altura inline do wrapper (mais confiável) ou altura armazenada
    let oldHeight;

    if (wrapperHasInlineHeight) {
      // Wrapper tem altura inline: usa essa altura (é o que está sendo exibido agora)
      oldHeight = wrapperRef.current.getBoundingClientRect().height;
    } else if (previousContentHeightRef.current === null) {
      // Primeira transição sem altura armazenada: não temos oldHeight
      oldHeight = null;
    } else {
      // Wrapper estava em auto mas acabamos de fixar: usa altura armazenada
      oldHeight = previousContentHeightRef.current;
    }

    // Aguarda o React completar o ciclo de renderização para capturar a NOVA altura
    animationTimeoutRef.current = setTimeout(() => {
      if (!wrapperRef.current || !contentRef.current) return;

      // If fullscreen or closing was activated while this timeout was
      // pending, skip height animation — closing especially: setting
      // `style.transition = 'height ...'` here would clobber the `transform`
      // transition useSlideAnimation just set for the slideOut (transition
      // is a single shorthand property).
      /* istanbul ignore next */
      if (isFullscreenRef.current || isClosingRef.current) return;

      // Agora captura a nova altura diretamente do conteúdo (já renderizado pelo React)
      const newHeight = contentRef.current.getBoundingClientRect().height;

      // Se oldHeight é null (primeira transição sem altura armazenada), não anima
      // Apenas fixa o wrapper na altura atual e armazena para próxima transição
      if (oldHeight === null) {
        wrapperRef.current.style.height = `${newHeight}px`;
        previousContentHeightRef.current = newHeight;
        lastAnimationStateRef.current = currentState;
        return;
      }

      // Re-measure the wrapper's CURRENT height now, instead of trusting
      // oldHeight (captured at the start of the effect, up to
      // RENDER_DELAY=300ms ago). If a resize animation ran in parallel
      // during that window — allowed by design, since status/route is
      // never blocked by it — oldHeight is now stale relative to what's
      // actually on screen. Freezing at that stale value causes a visible
      // jump backward before resuming toward the real target. currentHeight
      // reflects what's actually rendered at this exact moment, so the
      // freeze below never produces that jump.
      const currentHeight = wrapperRef.current.getBoundingClientRect().height;

      const diff = Math.abs(currentHeight - newHeight);

      // Se a altura não mudou significativamente (tolerância de 1px), não anima
      if (diff < 1) {
        lastAnimationStateRef.current = currentState;
        return;
      }

      // Fixa na altura atual sem transição (se ainda não estiver fixada)
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${currentHeight}px`;

      // Force reflow
      wrapperRef.current.getBoundingClientRect();

      // Reativa transição e anima para nova altura
      requestAnimationFrame(() => {
        if (wrapperRef.current) {
          markHeightAnimationStarted();

          wrapperRef.current.style.transition = `height ${
            ANIMATION_DURATIONS.BASE / 1000
          }s ease`;
          wrapperRef.current.style.height = `${newHeight}px`;

          // Armazena a nova altura para usar na PRÓXIMA transição
          previousContentHeightRef.current = newHeight;

          // Marca que animamos para este estado
          lastAnimationStateRef.current = currentState;
        }
      });
    }, ANIMATION_DURATIONS.RENDER_DELAY); // Aguardar React/browser completarem renderização (incluindo API)
    // Only `displayStatusType`/`displayLocationPathname` are dependencies —
    // `status`, `globalProgressBusy`, and `isShowingGlobalProgress` are all
    // still read below (via closure) but intentionally excluded as *triggers*
    // for this effect. All three can flip well before `displayStatusType`
    // does:
    //   - `status` changes the instant the caller sets it.
    //   - `globalProgressBusy` (see useProgressBusyState) flips as soon as
    //     GlobalProgress mounts and reports itself busy, unrelated to the
    //     fade-out timing.
    //   - `isShowingGlobalProgress` (computed in DynamicWrapper.jsx) ORs in
    //     `globalProgressBusy`, so it inherits the same premature flip.
    // `displayStatusType` alone only changes once useStatusTransition's
    // ANIMATION_DURATIONS.BASE (650ms) fade-out sequence actually completes
    // and the real content has been swapped for GlobalProgress in the DOM
    // (or vice-versa). Triggering on any of the other three re-introduces a
    // double/premature fire: during the fade-out window, ContentSwitcher
    // renders the outgoing content AND GlobalProgress as plain siblings (not
    // an overlay), so a measurement taken mid-window captures their summed
    // height instead of either one alone — and, without a later corrective
    // run, that wrong height sticks permanently (see git history for the
    // incident this comment documents).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayStatusType, displayLocationPathname]);

  /**
   * Aplica animação de altura após medir novo conteúdo no resize
   */
  const applyResizeAnimation = (newContentHeight) => {
    if (!wrapperRef.current) return;

    markHeightAnimationStarted();

    wrapperRef.current.style.transition = `height ${
      ANIMATION_DURATIONS.BASE / 1000
    }s ease`;
    wrapperRef.current.style.height = `${newContentHeight}px`;

    previousContentHeightRef.current = newContentHeight;
  };

  /**
   * Processa medições e decide se anima ou apenas atualiza altura
   */
  const processMeasurementsAndAnimate = (currentWrapperHeight) => {
    if (!wrapperRef.current || !contentRef.current) return;

    const newContentHeight =
      contentRef.current.getBoundingClientRect().height;

    if (headerContentRef?.current) {
      const headerHeight = headerContentRef.current.lastElementChild?.clientHeight ?? 0;
      if(isDesktop){
        wrapperRef.current.style.top = `${headerHeight}px`;
      }

      wrapperRef.current.style.maxHeight = `${window.innerHeight - headerHeight - 15}px`;
    }

    // Se a diferença for significativa (>= 1px), anima
    if (Math.abs(currentWrapperHeight - newContentHeight) >= 1) {
      // Garante o ponto de partida sem transição (o wrapper já está nessa
      // altura; reafirmar evita herdar uma transição pendente).
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${currentWrapperHeight}px`;

      // Force reflow
      wrapperRef.current.getBoundingClientRect();

      // Anima para nova altura
      requestAnimationFrame(() =>
        applyResizeAnimation(newContentHeight),
      );
    } else {
      wrapperRef.current.style.transition = 'none';
      wrapperRef.current.style.height = `${newContentHeight}px`;
      previousContentHeightRef.current = newContentHeight;
    }
  };

  /**
   * Executa recálculo de altura após debounce
   */
  const executeResizeRecalculation = () => {
    // Fullscreen ativo: não interferir com a animação de expansão
    if (isFullscreenRef.current) {
      return;
    }

    // Modal aberto (wrapper escalado): não remedir — a medição viria
    // escalada e encolheria o card. Ver isWrapperScaled.
    if (isWrapperScaled()) {
      return;
    }

    // A height animation triggered by a status/route change is already in
    // flight — let it finish instead of cutting it off mid-transition.
    // Record that a resize was dropped so markHeightAnimationStarted's
    // timeout can replay it once the guard clears.
    if (isAnimatingHeightRef.current) {
      hasPendingResizeRef.current = true;
      return;
    }

    // Captura a altura atual do wrapper (antes do resize)
    const currentWrapperHeight =
      wrapperRef.current.getBoundingClientRect().height;

    // A nova altura é medida diretamente no contentRef (filho), que tem
    // altura natural por si só. NÃO tornamos o wrapper height:auto: fazê-lo
    // pintava o card em 100% por um frame antes de voltar e animar — a
    // piscada vista no Safari mobile entre o hide do loading e o início da
    // animação de abertura. Mantendo o wrapper na altura atual, animamos
    // direto da altura antiga para a nova, sem frame intermediário.
    processMeasurementsAndAnimate(currentWrapperHeight);
  };

  /**
   * Handler para resize com debounce
   * Recalcula a altura do wrapper quando a janela muda de tamanho
   * ou quando o dispositivo muda de orientação
   */
  const handleResize = () => {
    // Verifica refs no momento do resize
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }

    // Limpa timeout anterior para implementar debounce
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Aguarda 150ms após o último evento de resize para processar
    resizeTimeoutRef.current = setTimeout(executeResizeRecalculation, 150);
  };

  // Effect para tratar resize da janela e mudanças de orientação
  useLayoutEffect(() => {
    // Adiciona listeners para resize e orientationchange
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }

      if (animationEndTimeoutRef.current) {
        clearTimeout(animationEndTimeoutRef.current);
        animationEndTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Array vazio: efeito roda apenas na montagem/desmontagem

  return { wrapperRef, contentRef };
};
